import "@styles/app.sass"
import { initializeClock } from "./partials/count-down";
import { Modal, ModalOpener } from "./partials/modal";
import Rellax from "rellax"

document.addEventListener("DOMContentLoaded", () => {
    initializeClock('[data-event-countdown]');

    if (document.querySelector('[data-parallax]')) {
        new Rellax('[data-parallax]', {
            center: true
        })
    }

    (() => { // Hiding header
        const header = document.querySelector('[data-page-header]') as HTMLElement
        if (!header) return

        const headerOffset = header.offsetTop - window.scrollY,
            headerHeight = (header.children[0] as HTMLElement)?.offsetHeight ?? 0

        let lastScroll = 0

        const hidingOnScroll = () => {
            const currentScroll = window.scrollY

            if (currentScroll > headerOffset + headerHeight) {
                header.classList.add('is-sticked')
                header.classList[currentScroll > lastScroll ? 'add' : 'remove']('is-up')
                document.body.classList[currentScroll > lastScroll ? 'remove' : 'add']('is-sticked-header')
            } else {
                header.classList.remove('is-sticked')
                header.classList.remove('is-up')
                document.body.classList.remove('is-sticked-header')
            }

            lastScroll = currentScroll
        }

        hidingOnScroll()
        window.addEventListener('scroll', () => hidingOnScroll(), { passive: true })

        const navOpener = document.querySelector<HTMLElement>('[data-nav-opener]')
        const closeNav = () => {
            document.body.classList.remove('u-hiddenOverflow')
            navOpener.closest('[data-page-header]')?.classList.remove('is-opened')
            navOpener.focus()
        }

        navOpener.addEventListener("click", () => {
            document.body.classList.toggle('u-hiddenOverflow')
            navOpener.closest('[data-page-header]')?.classList.toggle('is-opened')
        })

        document.addEventListener('keydown', (ev) => {
            if (ev.key === 'Escape' && navOpener.closest('[data-page-header]')?.classList.contains('is-opened')) {
                ev.preventDefault()
                closeNav()
            }
        })
    })();

    (() => { // Speaker modal
        new Modal("#Modal-speakerDetail")
        document.querySelectorAll('[data-modal-opener]')
            .forEach((el: HTMLElement) => new ModalOpener(el))
    })();

    (() => { // Program entry detail modal
        const modal = document.querySelector<HTMLDialogElement>('#Modal-programDetail')
        if (!modal) return

        let lastOpener: HTMLElement | null = null

        modal.addEventListener('close', () => {
            document.body.classList.remove('u-hiddenOverflow')
            lastOpener?.focus()
            lastOpener = null
        })
        modal.addEventListener('click', (ev) => (ev.target === modal) && modal.close())
        modal.querySelector('[data-modal-program-closer]')?.addEventListener('click', () => modal.close())

        document.querySelectorAll<HTMLButtonElement>('[data-program-opener]').forEach(btn => {
            btn.addEventListener('click', () => {
                lastOpener = btn
                const d = btn.dataset
                modal.querySelector('[data-program-time]').textContent  = d.time ?? ''
                modal.querySelector('[data-program-title]').textContent = d.title ?? ''
                modal.querySelector('[data-program-content]').innerHTML = d.content ?? ''

                const dayEl = modal.querySelector('[data-program-day]')
                if (dayEl) {
                    dayEl.textContent = d.day ?? ''
                    dayEl.classList.toggle('u-hidden', !d.day)
                }

                const stageWrapperEl = modal.querySelector('[data-program-stage-wrapper]')
                const stageEl = modal.querySelector('[data-program-stage]')
                if (stageEl && stageWrapperEl) {
                    stageEl.textContent = d.stage ?? ''
                    stageWrapperEl.classList.toggle('u-hidden', !d.stage)
                }

                const photosEl = modal.querySelector<HTMLElement>('[data-program-photos]')
                const speakersEl = modal.querySelector('[data-program-speakers]')
                
                if (photosEl) {
                    photosEl.querySelectorAll('img, figure').forEach(el => el.remove())
                }
                speakersEl.innerHTML = ''
                
                let speakers: { name: string; photo: string }[] = []
                try { speakers = JSON.parse(d.speakers ?? '[]') } catch {}

                if (speakers.length) {
                    if (photosEl) {
                        const afterSymbol = photosEl.querySelector('.EventProgramEntry-photoAfter')
                        speakers.forEach(s => {
                            let item: HTMLElement
                            if (s.photo) {
                                const temp = document.createElement('div')
                                temp.innerHTML = s.photo
                                item = temp.firstElementChild as HTMLElement
                            } else {
                                item = document.createElement('figure')
                                item.classList.add('is-placeholder')
                                item.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><use href="#icon-nophoto"/></svg>`
                            }
                            if (afterSymbol) {
                                photosEl.insertBefore(item, afterSymbol)
                            } else {
                                photosEl.appendChild(item)
                            }
                        })
                        photosEl.classList.toggle('EventProgramEntry-photo--many', speakers.length > 3)
                        photosEl.classList.remove('u-hidden')
                    }
                    
                    speakersEl.textContent = speakers.map(s => s.name).join(', ')
                    speakersEl.classList.remove('u-hidden')
                } else {
                    if (photosEl) photosEl.classList.add('u-hidden')
                    speakersEl.classList.add('u-hidden')
                }

                document.body.classList.add('u-hiddenOverflow')
                modal.showModal()
            })
        })
    })();

    (() => { // Stage + Day tab switching
        const stageTabs = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-stage-tab]'))
        const dayTabs   = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-day-tab]'))
        const dayPanels = Array.from(document.querySelectorAll<HTMLElement>('[data-day-panel]'))

        if (!dayTabs.length) return

        let activeDay   = 0
        let activeStage = stageTabs[0]?.dataset.stageTab ?? ''

        const updateView = () => {
            // Day panels: show only active
            dayPanels.forEach((panel, i) => {
                panel.classList.toggle('is-hidden', i !== activeDay)
            })
            dayTabs.forEach((tab, i) => {
                const active = i === activeDay
                tab.classList.toggle('is-active', active)
                tab.setAttribute('aria-selected', active ? 'true' : 'false')
            })

            // Stage tabs: highlight active
            stageTabs.forEach(tab => {
                const active = tab.dataset.stageTab === activeStage
                tab.classList.toggle('is-active', active)
                tab.setAttribute('aria-selected', active ? 'true' : 'false')
            })

            // Moderator: show only for active stage
            document.querySelectorAll<HTMLElement>('[data-stage-moderator]').forEach(el => {
                el.classList.toggle('is-active', el.dataset.stageModerator === activeStage)
            })

            // Filter entries in active panel by stage
            const activePanel = dayPanels[activeDay]
            if (!activePanel) return
            activePanel.querySelectorAll<HTMLElement>('.EventProgramEntry[data-stage]').forEach(entry => {
                const entryStage = entry.dataset.stage ?? ''
                // Empty stage = shared (breaks, lunch) — always visible
                entry.classList.toggle('is-hidden', entryStage !== '' && entryStage !== activeStage)
            })

            // Show/hide no-entry message based on visible entries
            const noEntryEl = activePanel.querySelector<HTMLElement>('.EventProgramNoEntry')
            if (noEntryEl) {
                const hasVisible = activePanel.querySelectorAll('.EventProgramEntry:not(.is-hidden)').length > 0
                noEntryEl.classList.toggle('is-hidden', hasVisible)
            }
        }

        // Init
        updateView()

        dayTabs.forEach((tab, i) => {
            tab.addEventListener('click', () => { activeDay = i; updateView() })
        })

        stageTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                activeStage = tab.dataset.stageTab ?? ''
                updateView()
            })
        })
    })();
})